
import { Gender } from '../enums';
import { averageValidNumbers } from '../shared-util';
import { LogRecordType, Log_EntryBase } from './user-log';

export interface Log_WeighIn extends Log_EntryBase {
  type: LogRecordType.WeighIn;
  weight_kg?: number;
  weight_unit?: string;
  tapeMeasures?: {
    waist_cm?: number[];
    waistMax_cm?: number /** DEPRECATED */;
    waistMin_cm?: number /** DEPRECATED */;
    unit?: string;
  };
  caliperMeasures?: {
    suprailiac_mm?: number[];
    umbilical_mm?: number[];
    thigh_mm?: number[];
  };
}

/// Average of all waist measurements for that log - or undefined if invalid/empty.
export function getAverageWaist(weighIn: Log_WeighIn): number | undefined {
  let t = weighIn && weighIn.tapeMeasures;
  if (!t) {
    return undefined;
  }
  let array = [t.waistMax_cm, t.waistMin_cm].concat(t.waist_cm);
  return averageValidNumbers(array, undefined);
}

/**
 * Most recent recorded waist circumference (in cm) taken from a newest-first
 * list of weigh-ins, or undefined if the user has no recorded waist value.
 *
 * Used to preselect the waist input in the daily weigh-in so its roller opens
 * on the user's last recorded value instead of the fixed `bodydimensions_cm`
 * default (35 cm) — the same way the weight and skinfold inputs already
 * preselect their last values. An entry counts as having a waist reading
 * only when its first reading is positive — the same `isFirstReadingPositive`
 * rule `hasMeasuredWaist` uses to decide an entry has real tape data.
 *
 * @param weighInsNewestFirst recent weigh-ins ordered most-recent first
 */
export function getLastMeasuredWaist_cm(weighInsNewestFirst: Log_WeighIn[]): number | undefined {
  // for each weigh-in from newest to oldest
  for (const weighIn of weighInsNewestFirst) {
    const waist = weighIn.tapeMeasures?.waist_cm;
    // if this entry has a real waist measurement, use its first reading
    if (isFirstReadingPositive(waist)) {
      return waist[0];
    }
  }
  return undefined;
}

/*
 * Two DIFFERENT questions get asked about the body-fat data on a weigh-in, and
 * the readers below keep them apart. Before these existed, both were written
 * out by hand as property chains at every call site, and the only thing telling
 * them apart was a trailing `[0] > 0` nobody could interpret in passing.
 *
 *  - "Does this entry hold any readings?" — `hasAnyTapeReadings` /
 *    `hasAnyCaliperReadings`. The array has to be there AND hold at least one
 *    slot; what those slots contain does not matter, so a row of zeroes or of
 *    blanks still answers YES. This is what decides whether the section is
 *    shown for editing.
 *  - "Did the user really measure something?" — `hasMeasuredWaist` /
 *    `hasMeasuredSkinfolds`, and `hasMeasuredBodyFat` for either of them. The
 *    first slot has to hold a number above zero, so a row of zeroes or of
 *    blanks answers NO. This is what decides whether body fat was recorded at
 *    all, and it drives the measurement cadence.
 *
 * So the case that separates the two pairs is an array that is PRESENT but
 * holds nothing real — which is what the roller writes when the user opens a
 * section and enters nothing. An absent array, and an array of length zero,
 * both answer NO to either question.
 *
 * `hasAnyCaliperReadings` and `hasMeasuredSkinfolds` ask about `suprailiac_mm`
 * ONLY, and that is deliberate rather than an oversight: calipers are always
 * recorded as a
 * complete tuple of sites, so the first site stands in for the whole set. An
 * entry carrying, say, only `thigh_mm` is not treated as a body-composition
 * record anywhere in this codebase — `hasBodyCompData` in `shared/src/body-comp-cadence.ts` says the same, and
 * the tests in `log-weigh-in.test.ts` pin it. Widening these readers to any
 * site would change which users count as having body-composition history, which
 * feeds the cadence, so it is a product decision and not a refactor.
 *
 * The deprecated tape fields `waistMax_cm` / `waistMin_cm` are ignored
 * throughout — new code is `waist_cm`-only.
 */

/**
 * Does this weigh-in hold at least one waist tape slot, whatever is in it?
 * Answers "should the waist section be shown", NOT "was a waist measured".
 */
export function hasAnyTapeReadings(weighIn: Log_WeighIn | undefined): boolean {
  return !!weighIn && Array.isArray(weighIn.tapeMeasures?.waist_cm) && weighIn.tapeMeasures.waist_cm.length > 0;
}

/**
 * Does this weigh-in hold at least one skinfold slot, whatever is in it?
 * Answers "should the skinfold section be shown", NOT "were skinfolds measured".
 */
export function hasAnyCaliperReadings(weighIn: Log_WeighIn | undefined): boolean {
  return !!weighIn && Array.isArray(weighIn.caliperMeasures?.suprailiac_mm) && weighIn.caliperMeasures.suprailiac_mm.length > 0;
}

/** Did the user really measure their waist on this weigh-in (first reading above zero)? */
export function hasMeasuredWaist(weighIn: Log_WeighIn | undefined): boolean {
  return !!weighIn && isFirstReadingPositive(weighIn.tapeMeasures?.waist_cm);
}

/** Did the user really measure their skinfolds on this weigh-in (first reading above zero)? */
export function hasMeasuredSkinfolds(weighIn: Log_WeighIn | undefined): boolean {
  return !!weighIn && isFirstReadingPositive(weighIn.caliperMeasures?.suprailiac_mm);
}

/**
 * Did the user really measure body fat on this weigh-in, by either method?
 * This is the "does this entry count as a body-fat record" question.
 */
export function hasMeasuredBodyFat(weighIn: Log_WeighIn | undefined): boolean {
  return hasMeasuredWaist(weighIn) || hasMeasuredSkinfolds(weighIn);
}

/// Sum across all caliper sites of average of all measurements at that site.
/// If any site is empty/invalid, returns undefined.
export function getAverageCalipersSum(weighIn: Log_WeighIn, gender: Gender): number | undefined {
  let sites = weighIn && weighIn.caliperMeasures && (gender === Gender.male ? [weighIn.caliperMeasures.suprailiac_mm, weighIn.caliperMeasures.umbilical_mm] : [weighIn.caliperMeasures.suprailiac_mm, weighIn.caliperMeasures.umbilical_mm, weighIn.caliperMeasures.thigh_mm]);
  if (!sites) {
    return undefined;
  }
  let sum = 0;
  for (let site of sites) {
    let siteAvg = averageValidNumbers(site, undefined);
    if (!siteAvg) {
      return undefined;
    }
    sum += siteAvg;
  }
  return sum / sites.length;
}

// True iff `arr` is a populated numeric array whose first reading is a
// positive number. This is the one definition of "has a real measurement?".
// Callers, all in this file: `hasMeasuredWaist` and `hasMeasuredSkinfolds`
// above, which is how every screen asks the question, and
// `getLastMeasuredWaist_cm`, which applies the same rule while looking for the
// newest entry to preselect from. Change the rule here and all three move
// together.
function isFirstReadingPositive(arr: number[] | undefined): arr is number[] {
  return Array.isArray(arr) && arr.length > 0 && arr[0] > 0;
}
